import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import { confirmAlert } from 'react-confirm-alert';
import debounce from 'lodash/debounce';
import truncate from 'lodash/truncate';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { useRootStore } from '../../../../mobx/bridge';
import styles from './SearchResultContact.module.css';
import * as utils from '../../../../helpers/utils';
import { getChannelIconFromContact } from '../../../../helpers/contactHelpers';
import CustomConfirmAlert from '../CustomConfirmAlert/CustomConfirmAlert';

const anchorOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

const transformOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};

const bubbleMenu = makeStyles({
  root: {
    display: 'flex',
    width: '198px',
    margin: '0 0 0 54px',
    padding: '5px 10px 11px',
    background: '#717171',
    border: '1px solid #dfdfdf',
    borderRadius: '3',
    boxShadow: 'none',
    overflow: 'visible',
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: '0',
      left: '25%',
      zIndex: '1001',
      width: '0',
      height: '0',
      border: '7px solid transparent',
      borderTopColor: '#717171',
      borderBottom: '0',
      marginLeft: '-7px',
      marginBottom: '-7px',
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: '0',
      left: '25%',
      zIndex: '1000',
      width: '0',
      height: '0',
      border: '8px solid transparent',
      borderTopColor: '#717171',
      borderBottom: '0',
      marginLeft: '-8px',
      marginBottom: '-8px',
    },
  },
});

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const SearchResultContact = observer(function SearchResultContact(props) {
  const { commonStore, channelTypeStore, settingsStore, sendingEvents } = useRootStore();
  const { t } = useTranslation();

  const { contact, showAddCustomer } = props;
  const [moreOptions, setMoreOptions] = React.useState(null);
  const initialsRef = React.useRef(null);

  const settings = settingsStore.getLoggedUserSettings;

  const openChannel = debounce(() => {
    commonStore.setChannelSearchInput('');

    const rawPhoneNumber = contact.number && contact.number.replace(/[^\d+]/g, '');

    const contactData = contact.data;
    const contactTypeId = contactData?.ContactType?.TypeId;

    let channelData;
    if (contactTypeId === utils.ContactTypeEnum.Customer.TypeId) {
      channelData = {
        _id: utils.generateGuid(),
        IsPublic: true,
        ChannelName: t('Common.TextMessageWithContact', { contactName: contact.label.trim() }),
        ChannelDescription: null,
        ChannelType: channelTypeStore.computedGetByTypeId(utils.ChannelTypeEnum.SMS.TypeId),
        Contacts: [commonStore.getLoggedUser, contactData],
        CustomerPhoneNumber: contactData.PhoneNumbers.find(
          (p) => p.TwilioFormat === `+1${rawPhoneNumber}`
        ),
        IsDeleted: false,
      };
    } else if (contactTypeId === utils.ContactTypeEnum.WebChatContact.TypeId) {
      channelData = {
        ChannelType: channelTypeStore.computedGetByTypeId(utils.ChannelTypeEnum.WebChat.TypeId),
        Contacts: [contactData],
      };
    }

    sendingEvents.socketEmitRoom(
      channelData,
      utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
    );
  }, 250);

  function showContactInfo() {
    setMoreOptions(initialsRef.current);
  }

  function closeShowOptions() {
    if (moreOptions) {
      setMoreOptions(null);
    }
  }

  function addToThisCustomer() {
    if (
      !contact.data.PhoneNumbers.find(
        (f) => f._id === settings.SelectedChannel.CustomerPhoneNumber._id
      )
    ) {
      if (contact.data.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId) {
        const updatedContact = {
          ...contact.data,
          PhoneNumbers: contact.data.PhoneNumbers.concat(
            settings.SelectedChannel.CustomerPhoneNumber
          ),
        };

        sendingEvents.socketEmitEditContactInfo(updatedContact);

        const newChannel = {
          _id: settings.SelectedChannel._id,
          IsPublic: true,
          ChannelName: t('Common.TextMessageWithContact', {
            contactName: updatedContact.getFullName.trim(),
          }),
          ChannelDescription: null,
          ChannelType: channelTypeStore.computedGetByTypeId(utils.ChannelTypeEnum.SMS.TypeId),
          Contacts: [updatedContact],
          CustomerPhoneNumber: settings.SelectedChannel.CustomerPhoneNumber,
          IsDeleted: false,
        };

        sendingEvents.socketEmitRoom(
          newChannel,
          utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
        );
      }
    } else {
      confirmAlert({
        customUI: ({ onClose }) => (
          <CustomConfirmAlert
            message={t('SearchResultContact.ContactAlreadyHasSamePhoneNumber')}
            falseText={t('Common.Close')}
            falseEvent={onClose}
          />
        ),
      });
    }
  }

  const isMoreOptionsOpen = Boolean(moreOptions);
  const popperClass = bubbleMenu();

  const channelIcon = getChannelIconFromContact(contact.data);

  const channelContainerStyles = [styles.channelContainer];
  if (contact.data.ContactType.TypeId === utils.ContactTypeEnum.WebChatContact.TypeId) {
    channelContainerStyles.push(styles.blueBackground);
  }

  const address = contact.data.Address;

  const phoneDescription = contact.data.PhoneNumbers.find(
    (f) => f.Number === contact.number
  )?.Description?.trim();

  return (
    <React.Fragment>
      <Grid component="div" container>
        <Grid component="div" container item xs={12} className={channelContainerStyles.join(' ')}>
          <button
            type="button"
            className={styles.buttonContainer}
            onClick={showAddCustomer ? showContactInfo : openChannel}
          >
            <Grid
              component="div"
              container
              direction="row"
              justify="flex-start"
              alignItems="center"
            >
              <Grid component="div" container item xs={12} alignItems="center">
                <img src={channelIcon} alt="Channel logo" className={styles.channelIcon} />

                <span className={styles.channelNameStyle} data-testid={contact.label}>
                  {contact.label}
                </span>

                {contact.number && contact.label !== contact.number ? (
                  <span className={styles.channelPhoneNumber}>{contact.number}</span>
                ) : null}

                {phoneDescription ? (
                  <span className={styles.phoneDescription}>
                    {`${truncate(phoneDescription, {
                      length: 25,
                    })}`}
                  </span>
                ) : null}
              </Grid>
            </Grid>
          </button>
        </Grid>
      </Grid>

      <Popover
        open={isMoreOptionsOpen}
        anchorEl={moreOptions}
        onClose={closeShowOptions}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        classes={{ paper: popperClass.root }}
      >
        <Grid component="div" container>
          <Grid component="div" item xs={12}>
            <span className={styles.bubbleText}>{contact.label}</span>
          </Grid>

          <Grid component="div" item xs={12}>
            {contact.data.PhoneNumbers.map((m) => (
              <span key={m._id} className={[styles.bubbleText, styles.spaced].join(' ')}>
                {`${m.Number} ${m.Description && m.Description !== '' ? m.Description : ''}`.trim()}
              </span>
            ))}
          </Grid>

          {address ? (
            <Grid component="div" item xs={12}>
              <span key={address._id} className={[styles.bubbleText, styles.spaced].join(' ')}>
                {`${address.Street} ${address.City} ${address.State} ${address.ZIP}`.trim()}
              </span>
            </Grid>
          ) : null}

          <Grid component="div" item xs={12}>
            <button type="button" className={styles.bubbleButton} onClick={addToThisCustomer}>
              {t('SearchResultContact.AddToThisCustomer')}
            </button>
          </Grid>
        </Grid>
      </Popover>
    </React.Fragment>
  );
});
SearchResultContact.whyDidYouRender = true;
SearchResultContact.displayName = 'SearchResultContact';

export default SearchResultContact;
